import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Plus, Minus, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import FAQAccordion from '../components/FAQAccordion';
import RelatedTools from '../components/RelatedTools';
import ResultDisplay from '../components/ResultDisplay';
import JsonLd from '../components/JsonLd';
import { sumTimes, subtractTimes, validateTime, minutesToTime } from '../utils/timeCalculations';
import { getCurrentMonthYear } from '../utils/currentDate';

const HomePage = () => {
  const [mode, setMode] = useState('somar');
  const [entries, setEntries] = useState([
    { id: 1, hours: 0, minutes: 0 },
    { id: 2, hours: 0, minutes: 0 },
    { id: 3, hours: 0, minutes: 0 },
    { id: 4, hours: 0, minutes: 0 },
    { id: 5, hours: 0, minutes: 0 },
    { id: 6, hours: 0, minutes: 0 }
  ]);
  const [timeA, setTimeA] = useState('00:00');
  const [timeB, setTimeB] = useState('00:00');

  const sumResult = useMemo(() => {
    if (mode !== 'somar') return null;
    for (const entry of entries) {
      const validation = validateTime(entry.hours, entry.minutes);
      if (!validation.valid) return null;
    }
    const result = sumTimes(entries);
    return { formatted: minutesToTime(result.totalMinutes), decimal: result.decimal.toFixed(2) };
  }, [entries, mode]);

  // Derive the error from the inputs instead of calling setState inside useMemo.
  const subtractCalc = useMemo(() => (
    mode === 'subtrair' ? subtractTimes(timeA, timeB, false) : null
  ), [timeA, timeB, mode]);
  const error = subtractCalc && subtractCalc.error ? subtractCalc.error : '';
  const subtractResult = subtractCalc && !subtractCalc.error
    ? { formatted: minutesToTime(subtractCalc.totalMinutes), decimal: subtractCalc.decimal.toFixed(2) }
    : null;

  const addEntry = () => setEntries([...entries, { id: Date.now(), hours: 0, minutes: 0 }]);
  const removeEntry = (id) => { if (entries.length > 2) setEntries(entries.filter(e => e.id !== id)); };
  const updateEntry = (id, field, value) => {
    const numValue = parseInt(value) || 0;
    setEntries(entries.map(e => e.id === id ? { ...e, [field]: numValue } : e));
  };
  const clearAll = () => {
    if (mode === 'somar') {
      setEntries([
        { id: 1, hours: 0, minutes: 0 }, { id: 2, hours: 0, minutes: 0 },
        { id: 3, hours: 0, minutes: 0 }, { id: 4, hours: 0, minutes: 0 },
        { id: 5, hours: 0, minutes: 0 }, { id: 6, hours: 0, minutes: 0 }
      ]);
    } else { setTimeA('00:00'); setTimeB('00:00'); }
  };

  const faqs = [
    { question: 'Como somar horas e minutos?', answer: 'Para somar horas e minutos, insira cada valor em uma linha da calculadora no modo "Somar Horas". O resultado aparece automaticamente em formato HH:MM e em decimal. Você pode adicionar quantas linhas precisar. Exemplo: 2h30 + 3h45 = 6h15 (6,25 decimal).' },
    { question: 'Como subtrair horas?', answer: 'No modo "Subtrair Horas", insira o horário maior (ex: 17:30) no primeiro campo e o menor (ex: 08:00) no segundo. O resultado mostra a diferença. Para turnos que cruzam a meia-noite, use nossa calculadora de subtração de horas com opção de turno noturno.' },
    { question: 'Como calcular horas trabalhadas?', answer: 'Subtraia o horário de entrada do de saída e desconte o intervalo. Exemplo: entrada 08:00, saída 17:00, intervalo 1h = 8 horas trabalhadas. Para cálculo completo com dias da semana e salário, use a Calculadora de Horas Trabalhadas.' },
    { question: 'Como converter horas em decimal?', answer: 'Divida os minutos por 60 e some às horas inteiras. Exemplo: 7h45min = 7 + (45÷60) = 7,75 horas. Cuidado: 30 minutos = 0,50 (não 0,30). Nossa calculadora faz essa conversão automaticamente.' },
    { question: 'Como calcular hora extra?', answer: 'A hora extra é o tempo trabalhado além da jornada normal (8h diárias / 44h semanais pela CLT). O valor mínimo é 50% a mais em dias úteis e 100% em domingos/feriados. Use nossa Calculadora de Horas Extras para o cálculo exato.' },
    { question: 'O que é banco de horas?', answer: 'Sistema de compensação previsto no Art. 59 da CLT onde horas extras são acumuladas para folgas futuras, em vez de pagas em dinheiro. O prazo é de até 6 meses (acordo individual) ou 1 ano (acordo coletivo). Saiba mais no guia Banco de Horas: Como Funciona.' },
    { question: 'Como funciona o adicional noturno?', answer: 'O adicional noturno é um acréscimo de no mínimo 20% sobre a hora diurna, para trabalho entre 22h e 5h (urbano). A hora noturna é reduzida: equivale a 52min30s. Use a Calculadora de Adicional Noturno para calcular o valor exato.' },
    { question: 'Quantas horas tem um dia?', answer: 'Um dia tem 24 horas, 1.440 minutos ou 86.400 segundos. Para converter horas em dias, divida por 24. Exemplo: 72 horas = 3 dias. Para mais conversões, use nosso Conversor de Horas em Dias.' },
    { question: '44 horas semanais são quantas por dia?', answer: 'Em 6 dias: 8h por dia (seg-sex) + 4h no sábado. Em 5 dias: 8h48min por dia (seg-sex). A CLT permite as duas formas. Use a Calculadora de Jornada de Trabalho para ver todas as opções.' },
    { question: 'Qual o limite de horas extras por dia?', answer: 'A CLT permite no máximo 2 horas extras por dia (Art. 59), totalizando 10 horas diárias. Exceções existem em casos de força maior (Art. 61). Veja mais em Quantas Horas Extras Posso Fazer por Dia.' }
  ];

  // Multiple structured data schemas for maximum SERP features
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question", "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  const webAppSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Calculadora de Horas Online",
    "url": "https://calculadoradehoras.net.br/",
    "description": "Calculadora de horas online grátis para somar e subtrair horas e minutos. Resultado em HH:MM e decimal. Ferramenta gratuita conforme a CLT.",
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "All",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "BRL" },
    "inLanguage": "pt-BR",
    "browserRequirements": "Requires JavaScript"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Início", "item": "https://calculadoradehoras.net.br/" }
    ]
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "Como Somar Horas e Minutos Online",
    "description": "Aprenda a somar horas e minutos usando a calculadora de horas online gratuita.",
    "step": [
      { "@type": "HowToStep", "position": 1, "name": "Escolha o modo", "text": "Clique em 'Somar Horas' ou 'Subtrair Horas' para selecionar a operação desejada." },
      { "@type": "HowToStep", "position": 2, "name": "Insira os valores", "text": "Digite as horas e minutos em cada linha. Adicione mais linhas se necessário." },
      { "@type": "HowToStep", "position": 3, "name": "Veja o resultado", "text": "O resultado aparece automaticamente em formato HH:MM e em decimal." },
      { "@type": "HowToStep", "position": 4, "name": "Copie o resultado", "text": "Use o botão Copiar Resultado para salvar na área de transferência." }
    ]
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Calculadora de Horas",
    "url": "https://calculadoradehoras.net.br/",
    "description": "Ferramentas online gratuitas para cálculo de horas, horas trabalhadas, horas extras, adicional noturno e banco de horas conforme a CLT.",
    "inLanguage": "pt-BR"
  };

  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>Calculadora de Horas Online: Somar e Subtrair Horas</title>
        <meta name="description" content="Calculadora de horas online grátis: some e subtraia horas e minutos, com resultado em HH:MM e decimal. Horas trabalhadas, extras e banco de horas CLT." />
        <meta name="robots" content="index, follow, max-snippet:-1, max-video-preview:-1, max-image-preview:large" />
        <link rel="canonical" href="https://calculadoradehoras.net.br/" />
        <meta property="og:title" content="Calculadora de Horas Online: Somar e Subtrair Horas" />
        <meta property="og:description" content="Calculadora de horas online grátis: some e subtraia horas e minutos, com resultado em HH:MM e decimal. Horas trabalhadas, extras e banco de horas CLT." />
        <meta property="og:url" content="https://calculadoradehoras.net.br/" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="Calculadora de Horas" />
        <meta property="og:image" content="https://calculadoradehoras.net.br/og-image.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Calculadora de Horas Online: Somar e Subtrair Horas" />
        <meta name="twitter:description" content="Calculadora de horas online grátis: some e subtraia horas e minutos, com resultado em HH:MM e decimal. Horas trabalhadas, extras e banco de horas CLT." />
        <link rel="alternate" hrefLang="pt-BR" href="https://calculadoradehoras.net.br/" />
        <link rel="alternate" hrefLang="x-default" href="https://calculadoradehoras.net.br/" />
      </Helmet>
      <JsonLd data={faqSchema} />
      <JsonLd data={webAppSchema} />
      <JsonLd data={breadcrumbSchema} />
      <JsonLd data={howToSchema} />
      <JsonLd data={orgSchema} />

      <Navigation />

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-dark-text mb-4">
            Calculadora de Horas Online Grátis
          </h1>
          <p className="text-lg text-body-text">
            Some e subtraia horas e minutos de forma rápida e precisa. Resultado em HH:MM e decimal.
          </p>
          <p className="text-sm text-body-text mt-2" data-testid="last-updated" suppressHydrationWarning>{`Última atualização: ${getCurrentMonthYear()}`}</p>
        </div>

        {/* Calculator Card — identical UI to original */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8">
          <div className="flex space-x-2 mb-6 border-b border-border-gray">
            <button onClick={() => setMode('somar')} className={`px-6 py-3 font-medium transition-colors ${mode === 'somar' ? 'text-blue-primary border-b-2 border-blue-primary' : 'text-body-text hover:text-blue-primary'}`} data-testid="mode-somar">
              <Plus className="inline mr-2" size={20} />Somar Horas
            </button>
            <button onClick={() => setMode('subtrair')} className={`px-6 py-3 font-medium transition-colors ${mode === 'subtrair' ? 'text-blue-primary border-b-2 border-blue-primary' : 'text-body-text hover:text-blue-primary'}`} data-testid="mode-subtrair">
              <Minus className="inline mr-2" size={20} />Subtrair Horas
            </button>
          </div>

          {mode === 'somar' && (
            <div className="space-y-4">
              {entries.map((entry, index) => (
                <div key={entry.id} className="flex items-center space-x-3" data-testid={`entry-row-${index}`}>
                  <span className="text-sm font-medium text-body-text w-6">{`${index + 1}.`}</span>
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-body-text">Horas</label>
                      <input type="number" value={entry.hours} onChange={(e) => updateEntry(entry.id, 'hours', e.target.value)} min="0" className="w-full h-12 px-3 text-center text-lg border-2 border-border-gray rounded-lg focus:border-blue-primary focus:outline-none" data-testid={`hours-input-${index}`} />
                    </div>
                    <div>
                      <label className="text-xs text-body-text">Minutos</label>
                      <input type="number" value={entry.minutes} onChange={(e) => updateEntry(entry.id, 'minutes', e.target.value)} min="0" max="59" className="w-full h-12 px-3 text-center text-lg border-2 border-border-gray rounded-lg focus:border-blue-primary focus:outline-none" data-testid={`minutes-input-${index}`} />
                    </div>
                  </div>
                  <button onClick={() => removeEntry(entry.id)} className="p-2 text-warning-red hover:bg-red-50 rounded-lg transition-colors" disabled={entries.length <= 2} data-testid={`remove-entry-${index}`}>
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
              <button onClick={addEntry} className="w-full py-3 border-2 border-dashed border-border-gray rounded-lg text-body-text hover:border-blue-primary hover:text-blue-primary transition-colors font-medium" data-testid="add-entry-button">
                + Adicionar linha
              </button>
            </div>
          )}

          {mode === 'subtrair' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">Horário Maior (ex: 17:30)</label>
                <input type="time" value={timeA} onChange={(e) => setTimeA(e.target.value)} className="w-full h-12 px-4 text-lg border-2 border-border-gray rounded-lg focus:border-blue-primary focus:outline-none" data-testid="time-a-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">Horário Menor (ex: 08:00)</label>
                <input type="time" value={timeB} onChange={(e) => setTimeB(e.target.value)} className="w-full h-12 px-4 text-lg border-2 border-border-gray rounded-lg focus:border-blue-primary focus:outline-none" data-testid="time-b-input" />
              </div>
              {error && <p className="text-warning-red text-sm">{error}</p>}
            </div>
          )}

          <div className="mt-6 flex space-x-4">
            <button onClick={clearAll} className="flex-1 h-12 px-6 bg-gray-100 hover:bg-gray-200 text-body-text rounded-lg font-bold transition-colors" data-testid="clear-button">
              Limpar Tudo
            </button>
          </div>

          {mode === 'somar' && sumResult && (
            <div className="mt-6">
              <ResultDisplay result={sumResult} label="Total" calculatorName="Calculadora de Horas" pageUrl="https://calculadoradehoras.net.br" />
            </div>
          )}
          {mode === 'subtrair' && subtractResult && (
            <div className="mt-6">
              <ResultDisplay result={subtractResult} label="Diferença" calculatorName="Calculadora de Horas" pageUrl="https://calculadoradehoras.net.br" />
            </div>
          )}
        </div>

        <AdSlot slotId="ad-slot-1" />

        {/* === NEW: Conversion Reference Table (missing from original — top competitors have this) === */}
        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Tabela de Conversão: Minutos para Decimal</h2>
          <p className="text-body-text leading-relaxed mb-4">
            Consulte a tabela abaixo para converter minutos em <strong>formato decimal</strong>, o padrão utilizado em folhas de pagamento e sistemas de RH. Para converter, divida os minutos por 60.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-light-bg">
                  <th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">Minutos</th>
                  <th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">Decimal</th>
                  <th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">Minutos</th>
                  <th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">Decimal</th>
                </tr>
              </thead>
              <tbody>
                {[[5,'0,08',35,'0,58'],[10,'0,17',40,'0,67'],[15,'0,25',45,'0,75'],[20,'0,33',50,'0,83'],[25,'0,42',55,'0,92'],[30,'0,50',60,'1,00']].map(([m1,d1,m2,d2], i) => (
                  <tr key={i} className="hover:bg-light-bg transition-colors">
                    <td className="p-3 border-b border-border-gray font-bold text-dark-text">{`${m1} min`}</td>
                    <td className="p-3 border-b border-border-gray text-body-text">{d1}</td>
                    <td className="p-3 border-b border-border-gray font-bold text-dark-text">{`${m2} min`}</td>
                    <td className="p-3 border-b border-border-gray text-body-text">{d2}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* === CONTENT SECTIONS (expanded from original) === */}
        <article className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Como Usar a Calculadora de Horas</h2>
            <p className="text-body-text leading-relaxed mb-4">
              A nossa <strong>calculadora de horas online</strong> foi desenvolvida para facilitar o cálculo de tempo no seu dia a dia. Com ela, você pode <strong>somar e subtrair horas e minutos</strong> de forma rápida e precisa, sem necessidade de fazer contas manuais ou usar planilhas complicadas.
            </p>
            <ol className="list-decimal list-inside space-y-2 text-body-text mb-4">
              <li>Escolha o modo desejado: <strong>Somar Horas</strong> ou <strong>Subtrair Horas</strong> clicando nas abas acima da calculadora.</li>
              <li>No modo Somar, insira os valores de horas e minutos em cada linha. Adicione quantas linhas precisar clicando em "Adicionar linha".</li>
              <li>No modo Subtrair, insira o horário maior no primeiro campo e o menor no segundo.</li>
              <li>O resultado aparece automaticamente em formato <strong>HH:MM</strong> e <strong>decimal</strong> (ex: 7h30min = 7,50 horas).</li>
              <li>Use o botão "Copiar Resultado" para salvar na área de transferência.</li>
            </ol>
            <p className="text-body-text leading-relaxed">
              O formato decimal é essencial para cálculos de folha de pagamento, pois a maioria dos sistemas de RH trabalha com horas em decimal. Por exemplo, 8h45min = 8,75 horas — facilitando a multiplicação pelo valor da hora. Para uma <a href="/calculadora-de-minutos" className="text-blue-primary hover:underline">calculadora focada em minutos</a>, temos uma ferramenta dedicada.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">O Que é o Cálculo de Horas e Por Que Ele Importa?</h2>
            <p className="text-body-text leading-relaxed mb-4">
              O cálculo de horas é uma operação fundamental no mundo do trabalho. Milhões de trabalhadores brasileiros precisam <strong>calcular horas trabalhadas</strong>, horas extras, intervalos e banco de horas diariamente. Um erro pode significar prejuízo financeiro, seja para o empregado que deixa de receber, seja para o empregador que paga a mais.
            </p>
            <p className="text-body-text leading-relaxed mb-4">
              No Brasil, a <a href="https://www.planalto.gov.br/ccivil_03/decreto-lei/del5452.htm" target="_blank" rel="noopener noreferrer" className="text-blue-primary hover:underline">Consolidação das Leis do Trabalho (CLT)</a> estabelece regras claras sobre a jornada. Conforme o <a href="https://www.planalto.gov.br/ccivil_03/decreto-lei/del5452.htm#art58" target="_blank" rel="noopener noreferrer" className="text-blue-primary hover:underline">Art. 58</a>, a jornada normal não excede 8 horas diárias e 44 horas semanais. Qualquer tempo além deve ser calculado como hora extra, com adicional mínimo de 50%. A CLT prevê tolerância de 5 minutos por registro de ponto, máximo de 10 minutos diários.
            </p>
            <p className="text-body-text leading-relaxed">
              Nossa calculadora de horas ajuda você a manter o controle preciso do tempo de trabalho. Para saber <a href="/jornada-de-trabalho" className="text-blue-primary hover:underline">quantas horas por dia sua jornada semanal exige</a>, ou entender <a href="/banco-de-horas-como-funciona" className="text-blue-primary hover:underline">como funciona o banco de horas</a>, temos guias completos. Consulte também o <a href="https://www.tst.jus.br/" target="_blank" rel="noopener noreferrer" className="text-blue-primary hover:underline">Tribunal Superior do Trabalho (TST)</a>.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Converter Horas e Minutos para Formato Decimal</h2>
            <p className="text-body-text leading-relaxed mb-4">
              A conversão para formato decimal é uma das funções mais usadas. A fórmula é simples: divida os minutos por 60. Veja exemplos práticos.
            </p>
            <ul className="list-disc list-inside space-y-2 text-body-text mb-4">
              <li>15 minutos = 15 ÷ 60 = <strong>0,25 horas</strong></li>
              <li>30 minutos = 30 ÷ 60 = <strong>0,50 horas</strong> (cuidado: não é 0,30!)</li>
              <li>45 minutos = 45 ÷ 60 = <strong>0,75 horas</strong></li>
              <li>20 minutos = 20 ÷ 60 = <strong>0,33 horas</strong></li>
            </ul>
            <p className="text-body-text leading-relaxed">
              Se você trabalhou 7h45min, o resultado decimal é 7,75 horas. Esse formato é o padrão dos sistemas de folha de pagamento e software de RH no Brasil. Para converter valores maiores como <a href="/converter-horas-em-dias" className="text-blue-primary hover:underline">72 horas em dias</a>, use nossa ferramenta de conversão.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Exemplos Práticos de Cálculo de Horas</h2>
            <div className="bg-light-bg p-6 rounded-lg mb-4">
              <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">Exemplo 1: Somando horas de trabalho da semana</h3>
              <p className="text-body-text leading-relaxed mb-2">
                João registrou: segunda 8h30, terça 7h45, quarta 8h15, quinta 9h00 e sexta 7h30. Ele insere cada valor em uma linha.
              </p>
              <p className="text-body-text leading-relaxed">
                <strong>Resultado:</strong> 41h00 (41,00h decimal). A jornada semanal é 44h, então João trabalhou 3h a menos — que podem ser compensadas via <a href="/calculadora-de-banco-de-horas" className="text-blue-primary hover:underline">banco de horas</a>.
              </p>
            </div>
            <div className="bg-light-bg p-6 rounded-lg mb-4">
              <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">Exemplo 2: Diferença entre dois horários</h3>
              <p className="text-body-text leading-relaxed mb-2">
                Maria entrou às 08:15 e saiu às 17:45. No modo Subtrair: 17:45 - 08:15.
              </p>
              <p className="text-body-text leading-relaxed">
                <strong>Resultado:</strong> 9h30 (9,50h decimal). Descontando 1h de almoço = 8h30min efetivas. Para verificar se há <a href="/calculadora-de-horas-extras" className="text-blue-primary hover:underline">horas extras</a>, considere a jornada contratada.
              </p>
            </div>
            <div className="bg-light-bg p-6 rounded-lg">
              <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">Exemplo 3: Calculando valor da hora trabalhada</h3>
              <p className="text-body-text leading-relaxed mb-2">
                Pedro ganha R$ 3.000 com jornada de 44h semanais (divisor 220).
              </p>
              <p className="text-body-text leading-relaxed">
                <strong>Valor da hora:</strong> R$ 3.000 ÷ 220 = R$ 13,64. Hora extra 50% = R$ 20,45. Use o <a href="/calculo-de-hora-trabalhada" className="text-blue-primary hover:underline">cálculo de hora trabalhada</a> para descobrir o seu.
              </p>
            </div>
          </section>

          {/* Full categorised tools hub — all 17 tools, 3 columns */}
          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-2">Todas as Ferramentas de Cálculo de Horas</h2>
            <p className="text-body-text leading-relaxed mb-6">
              Conjunto completo de calculadoras e guias gratuitos para cálculos trabalhistas conforme a CLT. Escolha a ferramenta certa para cada situação.
            </p>

            {/* Category 1 — Calculadoras */}
            <div className="mb-6">
              <h3 className="text-xs font-semibold text-body-text uppercase tracking-widest mb-3 flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-sm bg-blue-primary" />
                Calculadoras
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { to: '/calculadora-de-horas-trabalhadas', icon: '🕐', title: 'Horas Trabalhadas', desc: 'Jornada diária, semanal e mensal com resultado em R$.' },
                  { to: '/calculadora-de-horas-extras', icon: '💰', title: 'Horas Extras', desc: 'Adicional de 50% e 100% com pagamento em R$.' },
                  { to: '/calculadora-de-subtracao-de-horas', icon: '➖', title: 'Subtração de Horas', desc: 'Diferença entre dois horários, inclusive turno noturno.' },
                  { to: '/calculadora-de-tempo', icon: '⏱️', title: 'Calculadora de Tempo', desc: 'Somar, subtrair e converter qualquer duração de tempo.' },
                  { to: '/calculadora-de-minutos', icon: '⏲️', title: 'Calculadora de Minutos', desc: 'Somar minutos e converter para horas decimais.' },
                  { to: '/contador-de-horas', icon: '⏳', title: 'Contador de Horas', desc: 'Cronômetro ao vivo para contar horas de trabalho.' },
                ].map(({ to, icon, title, desc }) => (
                  <Link key={to} to={to} className="flex items-start gap-3 bg-white border border-border-gray rounded-xl p-4 hover:border-blue-primary hover:shadow-sm transition-all group">
                    <span className="text-2xl mt-0.5 flex-shrink-0">{icon}</span>
                    <div>
                      <p className="font-semibold text-dark-text text-sm group-hover:text-blue-primary transition-colors">{title}</p>
                      <p className="text-xs text-body-text mt-0.5 leading-relaxed">{desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Category 2 — CLT & Noturno */}
            <div className="mb-6">
              <h3 className="text-xs font-semibold text-body-text uppercase tracking-widest mb-3 flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-sm bg-indigo-500" />
                CLT &amp; Noturno
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { to: '/calculadora-de-adicional-noturno', icon: '🌙', title: 'Adicional Noturno', desc: 'Hora noturna reduzida (52min30s) e adicional de 20%.' },
                  { to: '/adicional-noturno-valor', icon: '💵', title: 'Valor do Adicional Noturno', desc: 'Calcule o valor exato em R$ do adicional noturno.' },
                  { to: '/adicional-noturno-horario', icon: '🌃', title: 'Horário do Adicional Noturno', desc: 'A partir de que horas começa e qual o percentual.' },
                  { to: '/calculadora-de-banco-de-horas', icon: '🏦', title: 'Banco de Horas', desc: 'Controle o saldo positivo e negativo do banco CLT.' },
                  { to: '/banco-de-horas-como-funciona', icon: '📖', title: 'Banco de Horas: Como Funciona', desc: 'Guia completo com regras, prazos e saldo negativo.' },
                ].map(({ to, icon, title, desc }) => (
                  <Link key={to} to={to} className="flex items-start gap-3 bg-white border border-border-gray rounded-xl p-4 hover:border-blue-primary hover:shadow-sm transition-all group">
                    <span className="text-2xl mt-0.5 flex-shrink-0">{icon}</span>
                    <div>
                      <p className="font-semibold text-dark-text text-sm group-hover:text-blue-primary transition-colors">{title}</p>
                      <p className="text-xs text-body-text mt-0.5 leading-relaxed">{desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Category 3 — Guias & Conversores */}
            <div>
              <h3 className="text-xs font-semibold text-body-text uppercase tracking-widest mb-3 flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-sm bg-success-green" />
                Guias &amp; Conversores
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { to: '/converter-horas-em-dias', icon: '📅', title: 'Converter Horas em Dias', desc: '72h, 48h, 36h são quantos dias? Tabela completa.' },
                  { to: '/jornada-de-trabalho', icon: '🗓️', title: 'Jornada de Trabalho', desc: '44h, 40h, 30h semanais são quantas horas por dia?' },
                  { to: '/calculo-de-hora-trabalhada', icon: '🧮', title: 'Valor da Hora Trabalhada', desc: 'Descubra quanto vale cada hora do seu trabalho.' },
                  { to: '/calculo-de-horas-extraordinarias', icon: '📋', title: 'Horas Extraordinárias', desc: 'Guia completo com cálculo passo a passo pela CLT.' },
                  { to: '/quantas-horas-extras-por-dia', icon: '⚠️', title: 'Limite de Horas Extras', desc: 'Quantas horas extras posso fazer por dia (CLT)?' },
                ].map(({ to, icon, title, desc }) => (
                  <Link key={to} to={to} className="flex items-start gap-3 bg-white border border-border-gray rounded-xl p-4 hover:border-blue-primary hover:shadow-sm transition-all group">
                    <span className="text-2xl mt-0.5 flex-shrink-0">{icon}</span>
                    <div>
                      <p className="font-semibold text-dark-text text-sm group-hover:text-blue-primary transition-colors">{title}</p>
                      <p className="text-xs text-body-text mt-0.5 leading-relaxed">{desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Dicas Importantes</h2>
            <ul className="list-disc list-inside space-y-3 text-body-text">
              <li><strong>Confira sempre os minutos:</strong> Um erro comum é digitar 75 minutos ao invés de 1h15min. Minutos vão até 59. A partir de 60, converta para horas.</li>
              <li><strong>Use o formato 24 horas:</strong> Evite confusão entre AM e PM. Use 14:30 ao invés de 2:30 PM.</li>
              <li><strong>Salve seus cálculos:</strong> Use "Copiar Resultado" antes de fechar a página.</li>
              <li><strong>Turno noturno:</strong> Se seu horário cruza a meia-noite (22:00 às 06:00), use a <Link to="/calculadora-de-subtracao-de-horas" className="text-blue-primary hover:underline">calculadora de subtração</Link> com turno noturno ativado. Saiba <Link to="/adicional-noturno-horario" className="text-blue-primary hover:underline">a partir de que horas começa o adicional noturno</Link>.</li>
              <li><strong>Limite de horas extras:</strong> A CLT permite <Link to="/quantas-horas-extras-por-dia" className="text-blue-primary hover:underline">máximo de 2 horas extras por dia</Link> (Art. 59), totalizando 10h diárias.</li>
            </ul>
          </section>
        </article>

        <AdSlot slotId="ad-slot-2" />

        <section className="my-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-6">Perguntas Frequentes</h2>
          <FAQAccordion faqs={faqs} />
        </section>

        <RelatedTools currentPath="/" />

        <AdSlot slotId="ad-slot-3" />
      </main>

      <Footer />
    </>
  );
};

export default HomePage;
